import type { Employee } from 'src/api/EmployeeService';
import type {
    TaskEmployee,
    TaskConfig,
} from 'src/api/ShiftGeneratorTaskService';
import type { DaySegment } from 'src/api/ShiftTypeService';
import type { RawCoverDemands, RawScheduleRequirement } from 'src/lib/types';
import { REMOVE_LEADING_ZEROES_REGEX } from '../regex';
import { DAY_SEGMENT_ORDER } from '../sorting';

export type EmployeesWithEmploymentInfo<T extends string | undefined = string> =
    Record<
        string,
        {
            name: T;
            positionName: string;
            vacancyRate: number;
            workingWeekMinutes: number;
            positionId: string;
            traits?: string[];
            color?: string;
        }
    >;

export type RequirementDetails = {
    minNrOfEmployees?: number;
    maxNrOfEmployees?: number;
    positions: Record<string, number>;
    traits: Record<string, number>;
};

export type RequirementsByKey<Key extends string | DaySegment> = Map<
    Key,
    RequirementDetails
>;

type RequirementsByDaySegment = RequirementsByKey<DaySegment>;
type RequirementsByShiftCode = RequirementsByKey<string>;

export type DaySegmentScheduleRequirements = Map<
    string, // date
    RequirementsByDaySegment
>;

export type ShiftCodeScheduleRequirements = Map<
    string, // date
    RequirementsByShiftCode
>;

export type ScheduleRequirements<IsByDaySegments extends boolean> =
    IsByDaySegments extends true
        ? DaySegmentScheduleRequirements
        : ShiftCodeScheduleRequirements;

export const getEmployeesWithEmploymentInfo = (
    employees: Employee[],
): EmployeesWithEmploymentInfo =>
    Object.fromEntries(
        employees?.map(
            ({
                id,
                name,
                vacancyRate,
                workingWeekMinutes,
                restPosition: { name: positionName, id: positionId, color },
            }) => [
                id,
                {
                    name,
                    positionName,
                    vacancyRate,
                    workingWeekMinutes,
                    positionId,
                    color,
                },
            ],
        ) ?? [],
    );

export const getTaskEmployeesWithEmploymentInfo = (
    taskEmployees: TaskEmployee[],
    employeeIdNameMap: Record<string, string>,
    positionIdNameMap: Record<string, string>,
): EmployeesWithEmploymentInfo<string | undefined> =>
    Object.fromEntries(
        taskEmployees?.map(
            ({
                id,
                vacancyRate,
                workingWeekMinutes,
                traits,
                position: positionId,
            }) => [
                id,
                {
                    name: employeeIdNameMap[id],
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    positionName: positionIdNameMap[positionId]!,
                    positionId,
                    vacancyRate,
                    workingWeekMinutes,
                    traits,
                },
            ],
        ) ?? [],
    );

const sumAndAggregateRecord = (
    target: Record<string, number>,
    additions: Iterable<[string, number]>,
): void => {
    for (const [key, value] of additions) {
        target[key] = (target[key] ?? 0) + value;
    }
};

type CoverDemandsAccumulator<IsByDaySegments extends boolean> =
    IsByDaySegments extends true
        ? RequirementsByDaySegment
        : RequirementsByShiftCode;

const getCoverDemands = <IsByDaySegments extends boolean>(
    rawCoverDemands: RawCoverDemands[],
    sortedCoverDemandKeys: string[] | DaySegment[],
    isByDaySegments: IsByDaySegments,
): CoverDemandsAccumulator<IsByDaySegments> => {
    const acc = new Map(
        sortedCoverDemandKeys.map(
            (key: string | DaySegment) =>
                [
                    key,
                    {
                        minNrOfEmployees: 0,
                        maxNrOfEmployees: 0,
                        positions: {},
                        traits: {},
                    },
                ] as [string | DaySegment, RequirementDetails],
        ),
    ) as CoverDemandsAccumulator<IsByDaySegments>;

    for (const [
        shiftCode,
        daySegment,
        minNrOfEmployees,
        maxNrOfEmployees,
        positions,
        traits,
    ] of rawCoverDemands) {
        const key = isByDaySegments ? daySegment : shiftCode;

        // TS can't know at compile time if key has type DaySegment or string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const coverDemands = acc.get(key as any);

        if (!coverDemands) {
            continue;
        }

        // Initialize if undefined (shouldn't happen with our initialization, but type-safe)
        coverDemands.minNrOfEmployees ??= 0;
        coverDemands.maxNrOfEmployees ??= 0;

        coverDemands.minNrOfEmployees += minNrOfEmployees;
        coverDemands.maxNrOfEmployees += maxNrOfEmployees;
        sumAndAggregateRecord(coverDemands.positions, positions);
        sumAndAggregateRecord(coverDemands.traits, traits);
    }

    return acc;
};

const sortCoverDemandKeys = (
    coverDemandKeys: (string | DaySegment)[],
    isByDaySegments: boolean,
    shiftCodeToDaySegment: Record<string, DaySegment>,
): (string | DaySegment)[] =>
    coverDemandKeys.sort((a: DaySegment | string, b: DaySegment | string) => {
        const aDaySegment = isByDaySegments ? a : shiftCodeToDaySegment[a];
        const bDaySegment = isByDaySegments ? b : shiftCodeToDaySegment[b];

        if (aDaySegment && bDaySegment) {
            const daySegmentDiff =
                DAY_SEGMENT_ORDER[aDaySegment] - DAY_SEGMENT_ORDER[bDaySegment];

            // primary sort: by day segment, secondary sort: alphabetical
            return daySegmentDiff !== 0 ? daySegmentDiff : a.localeCompare(b);
        }

        return 0;
    });

export const getSortedCoverDemandKeys = <IsByDaySegments extends boolean>(
    rawScheduleRequirements: RawScheduleRequirement[],
    isByDaySegments: IsByDaySegments,
): IsByDaySegments extends true ? DaySegment[] : string[] => {
    const coverDemandKeysSet = new Set<string>();
    const shiftCodeToDaySegment: Record<string, DaySegment> = {};

    for (const [, rawCoverDemandsOfDate] of rawScheduleRequirements) {
        for (const [shiftCode, daySegment] of rawCoverDemandsOfDate) {
            shiftCodeToDaySegment[shiftCode] = daySegment;
            const key = isByDaySegments ? daySegment : shiftCode;
            coverDemandKeysSet.add(key);
        }
    }

    const coverDemandKeys = Array.from(coverDemandKeysSet);

    return sortCoverDemandKeys(
        coverDemandKeys,
        isByDaySegments,
        shiftCodeToDaySegment,
    ) as IsByDaySegments extends true ? DaySegment[] : string[];
};

export const getScheduleRequirements = <
    IsByDaySegments extends boolean = false,
>(
    rawScheduleRequirements: RawScheduleRequirement[],
    sortedCoverDemandKeys: string[],
    isByDaySegments?: IsByDaySegments,
): ScheduleRequirements<IsByDaySegments> =>
    new Map(
        rawScheduleRequirements.map(
            ([date, rawCoverDemandsOfDate]: [string, RawCoverDemands[]]) => {
                const coverDemands = getCoverDemands(
                    rawCoverDemandsOfDate,
                    sortedCoverDemandKeys,
                    !!isByDaySegments,
                );

                return [date, coverDemands];
            },
        ),
    ) as ScheduleRequirements<IsByDaySegments>;

/**
 * Prepare TaskConfig to include only required data for generating schedule
 * requirements.
 * @param {TaskConfig} config Input TaskConfig coming from API
 * @param {string} orgId organization id, could be any temporal string
 * @returns {string} stringified lean config JSON
 */
export const prepareConfigForScheduleRequirements = (
    config: TaskConfig,
    orgId = '',
): string => {
    const {
        id,
        name,
        organization,
        holidays,
        halfHolidays,
        restMins,
        maxWorkingMinsRollingPeriod,
        f1RestMins,
        startDate,
        nrOfWeeks,
        shiftTypes,
        positions,
        traits,
        weeklyCoverDemands,
        dailyCoverPatches,
    } = config;
    return JSON.stringify({
        id,
        name,
        organization: organization ?? {
            orgId,
            holidays,
            halfHolidays,
            restMins,
            maxWorkingMinsRollingPeriod,
            f1RestMins,
        },
        startDate,
        nrOfWeeks,
        shiftTypes,
        positions,
        traits,
        weeklyCoverDemands,
        dailyCoverPatches,
    });
};

export const getDotDecimalNumber = (v: string): number | undefined =>
    v === '' ? undefined : Number(v.replace(',', '.'));

export const formatInputAsFloat = (
    e: React.ChangeEvent<HTMLInputElement>,
): void => {
    const input = e.target;
    const selectionStart = input.selectionStart;

    let newValue = input.value.replace(/,/g, '.');

    newValue = newValue.replace(/[^0-9.]/g, '');

    // Allow only one dot
    const parts = newValue.split('.');
    if (parts.length > 2) {
        newValue = `${parts[0]}.${parts.slice(1).join('')}`; // join extra parts without dots
    }

    newValue = newValue.replace(REMOVE_LEADING_ZEROES_REGEX, '$1');

    input.value = newValue;

    if (selectionStart === null) {
        return;
    }

    // Restore caret position
    const newCaretPosition = Math.min(selectionStart, newValue.length);
    input.setSelectionRange(newCaretPosition, newCaretPosition);
};

/**
 * Transforms a value into a whole number (0, 1, ...) or null.
 */
export const getNullOrWholeNumber = (value: string | null): number | null => {
    if (
        value === null ||
        value === undefined ||
        value.toString().trim() === ''
    ) {
        return null;
    }
    const num = Number(value);
    if (isNaN(num) || !isFinite(num) || num < 0) {
        return null;
    }
    return num;
};

/**
 * Transforms a value into a positive number (1, ...) or null.
 */
export const getNullOrPositiveNumber = (
    value: string | null,
): number | null => {
    const num = getNullOrWholeNumber(value);
    if (num === null || num === 0) {
        return null;
    }
    return num;
};

export interface ShiftToken {
    code: string | undefined;
    start: string | undefined;
    end: string | undefined;
}

/** Splits a single `<code>_<start>_<end>` entry of a shift plan. */
export const parseShiftToken = (shift: string): ShiftToken => {
    const [code, start, end] = shift.split('_');

    return { code, start, end };
};

export const shiftsStringToShiftCode = (shifts: string): string[] =>
    shifts
        .split(',')
        .map((shift) => parseShiftToken(shift).code ?? 'Incorrect format');

export interface StateShift {
    code: string;
    startMinutes: number;
    durationMinutes: number;
    endMinutes: number;
}

export const parseStateShift = (shift: string): StateShift => {
    const [code = '', start, duration] = shift.split('_');
    const startMinutes = Number(start ?? 0);
    const durationMinutes = Number(duration ?? 0);

    return {
        code,
        startMinutes,
        durationMinutes,
        endMinutes: startMinutes + durationMinutes,
    };
};

// state is sorted by employee ID, the rows follow the task's employee order
export const getStateEntries = (
    state: Record<string, string>,
    employeeIds: string[],
): [string, string][] =>
    employeeIds.flatMap((employeeId): [string, string][] => {
        const shifts = state[employeeId];

        return shifts === undefined ? [] : [[employeeId, shifts]];
    });

export const roundToDecimal = (value: number, decimal = 1): number =>
    Number(value.toFixed(decimal));

export const capitalizeFirstLetter = (str: string): string =>
    str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const formatMinutesToHoursAndMinutes = (minutes: number): string => {
    const roundedMinutes = Math.round(minutes);
    const sign = roundedMinutes < 0 ? '-' : '';
    const absoluteMinutes = Math.abs(roundedMinutes);
    const remainingMinutes = absoluteMinutes % 60;
    const hours = (absoluteMinutes - remainingMinutes) / 60;

    return `${sign}${hours}:${remainingMinutes.toString().padStart(2, '0')}`;
};

export const indexEmployeesById = (
    employees: Employee[],
): Record<string, Employee> =>
    Object.fromEntries(
        employees?.map((employee) => [employee.id, employee]) ?? [],
    ) as Record<string, Employee>;
